import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  addMilestone,
  addProject,
  deleteProject,
  emptyProjects,
  findProject,
  getProjectsDoc,
  listDeletedProjects,
  listProjects,
  moveMilestone,
  removeMilestone,
  restoreProject,
  toggleMilestone,
  updateMilestone,
  updateProject,
} from '../../db/projects';
import { listAll } from '../../db/entries';
import { getCategoryMap } from '../../db/categories';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { EntryCard } from '../components/EntryCard';
import { CATEGORY_COLORS } from '../components/CatIcon';
import { AddInput, CommitInput } from '../components/CommitInput';
import {
  daysUntil,
  PROJECT_STATUS_LABELS,
  projectProgress,
  type Category,
  type Project,
  type ProjectsDoc,
  type ProjectStatus,
} from '../../domain/types';
import type { EntryRecord } from '../../db/schema';

function DeadlineChip({ deadline }: { deadline: string | null }) {
  if (!deadline) return null;
  const d = daysUntil(deadline, new Date());
  const over = d != null && d < 0;
  const near = d != null && d >= 0 && d <= 7;
  return (
    <span className={`deadline${over ? ' is-over' : near ? ' is-near' : ''}`}>
      {deadline.slice(5).replace('-', '/')}
      {d != null && (over ? `（${-d}日超過）` : d === 0 ? '（今日）' : `（あと${d}日）`)}
    </span>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  return (
    <div className="progress" role="img" aria-label={`進捗 ${percent}%`}>
      <div className="progress-fill" style={{ width: `${percent}%` }} />
    </div>
  );
}

export function ProjectsView() {
  const version = useDataVersion();
  const doc = useAsync<ProjectsDoc>(getProjectsDoc, [version], emptyProjects());
  const entries = useAsync<EntryRecord[]>(listAll, [version], []);
  const navigate = useNavigate();
  const [showDeleted, setShowDeleted] = useState(false);

  const projects = listProjects(doc);
  const deleted = listDeletedProjects(doc);
  const countByProject = new Map<string, number>();
  for (const e of entries) {
    if (e.projectId) countByProject.set(e.projectId, (countByProject.get(e.projectId) ?? 0) + 1);
  }

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <h1>プロジェクト</h1>
          <span className="summary">締め切りと進み具合をまとめて見ます</span>
        </div>
      </header>

      <div className="view-body projects">
        <AddInput
          placeholder="プロジェクト名を入れて追加"
          onAdd={async (value) => {
            const id = await addProject({ name: value });
            navigate(`/projects/${id}`);
          }}
        />

        {projects.length === 0 && deleted.length === 0 ? (
          <div className="empty">
            <p>プロジェクトがまだありません</p>
          </div>
        ) : (
          <ul className="project-list">
            {projects.map((p) => {
              const { percent, derived } = projectProgress(p);
              const openMs = p.milestones.filter((m) => !m.done);
              const next = [...openMs].sort((a, b) =>
                (a.due ?? '9999') < (b.due ?? '9999') ? -1 : 1,
              )[0];
              return (
                <li key={p.id} className={`project-card status-${p.status}`}>
                  <Link to={`/projects/${p.id}`} className="project-main">
                    <span className="project-dot" style={{ background: p.color }} />
                    <span className="project-body">
                      <span className="project-name">{p.name}</span>
                      <span className="project-meta muted">
                        <span className={`status-chip status-${p.status}`}>
                          {PROJECT_STATUS_LABELS[p.status]}
                        </span>
                        <DeadlineChip deadline={p.deadline} />
                        {p.milestones.length > 0 && (
                          <span>
                            マイルストーン {p.milestones.filter((m) => m.done).length}/
                            {p.milestones.length}
                          </span>
                        )}
                        <span>記録 {countByProject.get(p.id) ?? 0}件</span>
                      </span>
                      {next && (
                        <span className="project-next muted">
                          次: {next.title}
                          {next.due && ` — ${next.due.slice(5).replace('-', '/')}`}
                        </span>
                      )}
                      <ProgressBar percent={percent} />
                      <span className="project-percent muted">
                        {percent}%{derived ? '（マイルストーンから算出）' : ''}
                      </span>
                    </span>
                  </Link>
                  <Link className="ghost-btn project-write" to={`/new?project=${p.id}`}>
                    ✎ 記録する
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        {deleted.length > 0 && (
          <div className="field">
            <button className="ghost-btn" onClick={() => setShowDeleted((v) => !v)}>
              削除したプロジェクト（{deleted.length}）を{showDeleted ? '隠す' : '表示'}
            </button>
            {showDeleted && (
              <ul className="cat-list deleted-list">
                {deleted.map((p) => (
                  <li key={p.id} className="is-deleted">
                    <i className="swatch" style={{ background: p.color }} />
                    <span className="cat-name">{p.name}</span>
                    <span className="muted cat-count">記録 {countByProject.get(p.id) ?? 0}件</span>
                    <span className="cat-actions">
                      <button className="icon-btn" onClick={() => restoreProject(p.id)}>
                        戻す
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="muted">
              削除しても記録は残ります。ここから戻せます。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProjectDetailView() {
  const { projectId = '' } = useParams();
  const version = useDataVersion();
  const navigate = useNavigate();
  const doc = useAsync<ProjectsDoc>(getProjectsDoc, [version], emptyProjects());
  const categories = useAsync<Map<string, Category>>(getCategoryMap, [version], new Map());
  const allEntries = useAsync<EntryRecord[]>(listAll, [version], []);
  const [msDue, setMsDue] = useState('');

  const p: Project | undefined = findProject(doc, projectId);
  if (!p) {
    return (
      <div className="view">
        <div className="view-body empty">
          <p>プロジェクトが見つかりません</p>
          <Link className="primary-btn" to="/projects">
            一覧へ
          </Link>
        </div>
      </div>
    );
  }

  const { percent, derived } = projectProgress(p);
  const entries = allEntries
    .filter((e) => e.projectId === p.id)
    .sort((a, b) => (a.date < b.date ? 1 : -1));
  const milestones = [...p.milestones].sort((a, b) => a.order - b.order);

  return (
    <div className="view">
      <header className="view-head">
        <Link className="nav-btn" to="/projects" aria-label="戻る">
          ‹
        </Link>
        <div className="view-title">
          <h1>{p.name}</h1>
          <span className="summary">
            {PROJECT_STATUS_LABELS[p.status]} / {percent}%
            {derived ? '（マイルストーンから算出）' : ''}
          </span>
        </div>
        <Link className="primary-btn" to={`/new?project=${p.id}`}>
          ✎ 記録する
        </Link>
      </header>

      <div className="view-body project-detail">
        <ProgressBar percent={percent} />

        <div className="field-grid">
          <label className="field">
            <span className="field-label">状態</span>
            <select
              value={p.status}
              onChange={(e) => updateProject(p.id, { status: e.target.value as ProjectStatus })}
            >
              {(['active', 'onhold', 'done'] as ProjectStatus[]).map((s) => (
                <option key={s} value={s}>
                  {PROJECT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field-label">締め切り</span>
            <input
              type="date"
              value={p.deadline ?? ''}
              onChange={(e) => updateProject(p.id, { deadline: e.target.value || null })}
            />
          </label>

          <label className="field">
            <span className="field-label">色</span>
            <div className="swatch-grid">
              {CATEGORY_COLORS.map((c) => (
                <button
                  key={c}
                  className={`swatch-btn${p.color === c ? ' is-on' : ''}`}
                  style={{ background: c }}
                  onClick={() => updateProject(p.id, { color: c })}
                  aria-label={c}
                />
              ))}
            </div>
          </label>
        </div>

        <label className="field">
          <span className="field-label">メモ</span>
          <textarea
            rows={3}
            defaultValue={p.description}
            placeholder="目的・背景・関係者など"
            onBlur={(e) => {
              if (e.target.value !== p.description) updateProject(p.id, { description: e.target.value });
            }}
          />
        </label>

        {p.milestones.length === 0 && (
          <label className="field">
            <span className="field-label">進捗（手動）</span>
            <div className="field-row">
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={p.manualProgress}
                onChange={(e) => updateProject(p.id, { manualProgress: Number(e.target.value) })}
              />
              <span className="muted">{p.manualProgress}%</span>
            </div>
            <span className="field-hint">
              マイルストーンを追加すると、進捗はその完了率から自動で計算されます
            </span>
          </label>
        )}

        <div className="field">
          <span className="field-label">マイルストーン</span>
          {milestones.length > 0 && (
            <ul className="ms-list">
              {milestones.map((m, i) => (
                <li key={m.id} className={m.done ? 'is-done' : ''}>
                  <input
                    type="checkbox"
                    checked={m.done}
                    onChange={(e) => toggleMilestone(p.id, m.id, e.target.checked)}
                  />
                  <CommitInput
                    className="ms-title"
                    value={m.title}
                    onCommit={(title) => updateMilestone(p.id, m.id, { title })}
                  />
                  <input
                    type="date"
                    value={m.due ?? ''}
                    onChange={(e) => updateMilestone(p.id, m.id, { due: e.target.value || null })}
                  />
                  {!m.done && <DeadlineChip deadline={m.due} />}
                  <span className="cat-actions">
                    <button className="icon-btn" onClick={() => moveMilestone(p.id, m.id, -1)} disabled={i === 0} aria-label="上へ">↑</button>
                    <button className="icon-btn" onClick={() => moveMilestone(p.id, m.id, 1)} disabled={i === milestones.length - 1} aria-label="下へ">↓</button>
                    <button
                      className="icon-btn danger"
                      aria-label="削除"
                      onClick={() => {
                        if (confirm(`「${m.title}」を削除します。`)) removeMilestone(p.id, m.id);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <AddInput
            placeholder="マイルストーンを追加"
            onAdd={(value) => addMilestone(p.id, value, msDue || null)}
            onReset={() => setMsDue('')}
          >
            <input
              type="date"
              value={msDue}
              aria-label="期日"
              onChange={(e) => setMsDue(e.target.value)}
            />
          </AddInput>
        </div>

        <div className="field">
          <span className="field-label">このプロジェクトの記録（{entries.length}件）</span>
          {entries.length === 0 ? (
            <p className="muted">まだありません。「記録する」から書けます。</p>
          ) : (
            <div className="card-list">
              {entries.map((e) => (
                <EntryCard key={e.id} entry={e} categories={categories} projects={doc} />
              ))}
            </div>
          )}
        </div>

        <div className="danger-zone">
          <button
            className="danger-btn"
            onClick={async () => {
              if (
                !confirm(
                  `「${p.name}」を削除します。\n記録は消えません。一覧から戻すこともできます。`,
                )
              )
                return;
              await deleteProject(p.id);
              navigate('/projects', { replace: true });
            }}
          >
            このプロジェクトを削除
          </button>
        </div>
      </div>
    </div>
  );
}
